import type { Chart, ChartData, ChartOptions } from 'chart.js';
import { useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import type { ReportRefItemType } from 'types/api/quizReportRefResponseType';
import {
  buildChartData,
  buildChartOptions,
  extractChartValues,
  type ChartType,
} from './wsReportChart.utils';

// react-chartjs-2 の ChartJSOrUndefined 相当の型（index.d.ts に未 export のため直接定義）
type ChartJSOrUndefined<TType extends 'line'> = Chart<TType> | undefined;

export interface UseWsReportChartResult {
  initialized: boolean;
  chartType: ChartType | null;
  chartData: ChartData<'line'> | null;
  chartOptions: ChartOptions<'line'> | null;
  chartRef: MutableRefObject<ChartJSOrUndefined<'line'> | null>;
  onChangeChartType: (chartType: ChartType) => void;
}

export function useWsReportChart(
  reports: ReportRefItemType[],
  lineFlag: boolean
): UseWsReportChartResult {
  const [initialized, setInitialized] = useState(false);
  const [chartType, setChartType] = useState<ChartType | null>(null);
  const [chartData, setChartData] = useState<ChartData<'line'> | null>(null);
  const [chartOptions, setChartOptions] = useState<ChartOptions<'line'> | null>(null);
  const chartRef = useRef<ChartJSOrUndefined<'line'>>(null);

  const drawChart = (type: ChartType) => {
    const values = extractChartValues(reports, type, lineFlag);
    setChartData(buildChartData(reports, type, lineFlag));
    setChartOptions(buildChartOptions(values, type));
    if (chartRef.current) {
      chartRef.current.update();
    }
  };

  useEffect(() => {
    if (chartType === null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- 初回マウント時のチャートタイプ初期化のため意図的
      setChartType('participatedCount');
      drawChart('participatedCount');
      setInitialized(true);
    } else {
      drawChart(chartType);
    }
    // drawChart は render ごとに再生成される関数のため依存配列に含めると毎 render で発火してしまう。
    // reports/lineFlag 変更時のみチャートを再描画する現行挙動を保持するために意図的に除外している。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports, lineFlag]);

  const onChangeChartType = (type: ChartType) => {
    setChartType(type);
    drawChart(type);
  };

  return {
    initialized,
    chartType,
    chartData,
    chartOptions,
    chartRef,
    onChangeChartType,
  };
}
